import { useEffect, useCallback, type ReactNode } from 'react';
import { useAppShellDocument } from '@/lib/app-shell';
import { useQueryClient } from '@tanstack/react-query';
import { Outlet, Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { LayoutDashboard, Bell, Users, User, LogOut, CreditCard } from 'lucide-react';
import { cn, CompassLogo } from '@jeraisonne/ui';
import { NavBadge } from '@/components/nav-badge';
import { useFeedUnreadCount } from '@/lib/news/use-role-news-feed';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase';

// `short` : libellé de la barre du bas (5 onglets sur 375 px, pas de troncature).
const NAV_ITEMS = [
  {
    id: 'dashboard',
    label: 'Accueil',
    short: 'Accueil',
    icon: LayoutDashboard,
    to: '/parent/dashboard',
  },
  { id: 'news', label: 'Feed', short: 'Feed', icon: Bell, to: '/parent/news' },
  { id: 'children', label: 'Mes enfants', short: 'Enfants', icon: Users, to: '/parent/children' },
  {
    id: 'subscriptions',
    label: 'Abonnements',
    short: 'Abos',
    icon: CreditCard,
    to: '/parent/subscriptions',
  },
  { id: 'profile', label: 'Profil', short: 'Profil', icon: User, to: '/profile' },
] as const;

export function ParentLayout() {
  const { profile, loading, profileLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading || profileLoading) return;
    if (!profile) {
      navigate({ to: '/onboarding' });
      return;
    }
    if (profile.role !== 'parent') {
      navigate({ to: '/' });
    }
  }, [loading, profileLoading, profile, navigate]);

  if (loading || profileLoading || !profile || profile.role !== 'parent') return null;

  return (
    <ParentShell>
      <Outlet />
    </ParentShell>
  );
}

/**
 * Coquille de l'espace parent (barre latérale desktop + onglets mobiles).
 * Exportée pour `/profile`, hors de `/parent/*` : le parent y garde sa
 * navigation au lieu d'une page sans issue.
 */
export function ParentShell({ children }: { children: ReactNode }) {
  // La page ne défile pas (seul le contenu) : barre du bas stable après le clavier iOS.
  useAppShellDocument();
  const { profile } = useAuth();
  const navigate = useNavigate();
  const location = useRouterState({ select: (s) => s.location.pathname });
  // Nouveautés du Feed depuis sa dernière ouverture (masqué sur la page Feed).
  const feedUnread = useFeedUnreadCount('parent');
  const feedBadge = location.startsWith('/parent/news') ? 0 : feedUnread;
  const queryClient = useQueryClient();

  const handleSignOut = useCallback(async () => {
    queryClient.clear();
    await supabase.auth.signOut();
    navigate({ to: '/login' });
  }, [navigate, queryClient]);

  return (
    <div className="flex h-dvh overflow-hidden bg-transparent">
      {/* ── Desktop sidebar ──────────────────────────────────────────────── */}
      <aside className="border-ink-200 native:pt-[env(safe-area-inset-top)] native:pb-[env(safe-area-inset-bottom)] hidden w-60 shrink-0 flex-col border-r bg-white md:flex">
        <div className="px-5 pb-6 pt-5">
          <CompassLogo size="md" subtitle="Espace parent" animated />
        </div>

        <nav className="flex flex-col gap-0.5 px-3">
          {NAV_ITEMS.map(({ id, label, icon: Icon, to }) => {
            const active = location.startsWith(to);
            return (
              <Link
                key={id}
                to={to}
                className={cn(
                  'font-display flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-[13px] font-semibold transition-colors',
                  active ? 'bg-primary-50 text-primary-700' : 'text-ink-700 hover:bg-ink-100',
                )}
              >
                <Icon
                  className={cn(
                    'h-[18px] w-[18px] shrink-0',
                    active ? 'text-primary-600' : 'text-ink-500',
                  )}
                />
                {label}
                {id === 'news' && <NavBadge count={feedBadge} />}
              </Link>
            );
          })}
        </nav>

        <div className="bg-ink-100 mx-3 mb-4 mt-auto rounded-xl p-3.5">
          <p className="font-display text-ink-500 mb-1 text-[11px] font-bold uppercase tracking-widest">
            Connecté en tant que
          </p>
          <p className="font-display text-ink-900 truncate text-[14px] font-extrabold leading-tight">
            {profile?.display_name ?? 'Parent'}
          </p>
          <div className="mt-1.5 flex items-center justify-between">
            <Link to="/profile" className="text-primary-600 font-sans text-[11px] hover:underline">
              Mon profil
            </Link>
            <button
              onClick={handleSignOut}
              className="text-ink-500 hover:text-accent-600 flex items-center gap-1 font-sans text-[11px] transition-colors"
            >
              <LogOut className="h-3 w-3" />
              Déconnexion
            </button>
          </div>
        </div>
      </aside>

      {/* ── Content ─────────────────────────────────────────────────────── */}
      <div className="flex min-w-0 flex-1 flex-col">
        <main className="native:pt-[env(safe-area-inset-top)] native:md:pb-[env(safe-area-inset-bottom)]! flex-1 overflow-y-auto pb-20 md:pb-0">
          {children}
        </main>

        {/* ── Mobile bottom tab bar ────────────────────────────────────── */}
        <nav className="border-ink-200 native:pb-[min(env(safe-area-inset-bottom),2.125rem)] ocean:bg-[#061433] ocean:shadow-[0_-4px_20px_rgba(0,0,0,0.35)] fixed inset-x-0 bottom-0 z-10 flex border-t bg-white pb-[env(safe-area-inset-bottom)] shadow-[0_-4px_20px_rgba(5,102,242,0.08)] md:hidden">
          {NAV_ITEMS.map(({ id, short, icon: Icon, to }) => {
            const active = location.startsWith(to);
            return (
              <Link
                key={id}
                to={to}
                className={cn(
                  'font-display flex flex-1 flex-col items-center gap-1 pb-2 pt-2.5 text-[10px] font-semibold transition-colors',
                  active ? 'text-primary-600' : 'text-ink-400',
                )}
              >
                <span className="relative">
                  <Icon className={cn('h-5 w-5', active ? 'text-primary-600' : 'text-ink-400')} />
                  {id === 'news' && <NavBadge count={feedBadge} variant="icon" />}
                </span>
                <span className="text-center leading-tight">{short}</span>
              </Link>
            );
          })}
        </nav>
      </div>
    </div>
  );
}
